import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AutoComplete, Button, Spin } from "antd";
import { CloseSquareFilled, CompassOutlined } from "@ant-design/icons";
import type { DefaultOptionType } from "antd/es/select";
import usePlacesAutocomplete, { GeocodeResult, getGeocode, getLatLng } from "use-places-autocomplete";

import googlelogo from "@img/PoweredByGoogleOnWhite.png";
import { getMapsLoader } from "@/firebase";
import { Coordinates } from "@/models";
import { filterUpdateEvent } from "@/utils/analytics/googleAnalytics";

import "./style.css";

type LocationValue = {
    coordinates?: Coordinates;
    locationDisplayName?: string;
};

type LocationAutocompleteProps = {
    value?: LocationValue;
    onChange?: (value: LocationValue) => void;
    onPlaceChange?: (coords: Coordinates, displayName: string) => void;
    initialDisplayValue?: string | null;
};

function useGoogleMapsPlacesApi(onLoad: () => void) {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getMapsLoader().then(() => {
            setLoading(false);
            onLoad();
        });
    });

    return { loading };
}

function PoweredByGoogleWrapper(children: React.ReactNode) {
    return (
        <>
            {children}
            <img className="powered-by-google" src={googlelogo} alt="powered by google" />
        </>
    );
}

function UseCurrentLocation(props: {
    setValue: (coords: Coordinates, address: string) => void;
    onError: (code: number) => void;
}) {
    const { setValue, onError } = props;

    const formattedAddress = (result: GeocodeResult): string => {
        const addressComponents = result.address_components;
        const cityOrTown = addressComponents.find(
            (add) => add.types.includes("locality") || add.types.includes("administrative_area_level_3")
        )?.long_name;
        const zipcode = addressComponents.find((add) => add.types.includes("postal_code"))?.long_name;
        const stateAbbv = addressComponents.find((add) =>
            add.types.includes("administrative_area_level_1")
        )?.short_name;
        const country = addressComponents.find((add) => add.types.includes("country"))?.short_name;

        const componentsForFormatting = [cityOrTown, zipcode, stateAbbv, country].filter((value) => value);

        return componentsForFormatting.join(", ");
    };

    const getAddressFromPosition = useCallback(() => {
        if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const coordinates = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    getGeocode({ location: coordinates }).then((results) => {
                        const result = results.at(0);
                        if (result) {
                            setValue(coordinates, formattedAddress(result));
                        }
                    });
                    onError(0); // Clear any previous error
                },
                (error) => onError(error.code)
            );
        } else {
            console.log("Geolocation not supported");
        }
    }, [setValue, onError]);

    return (
        <Button type="text" icon={<CompassOutlined />} onClick={getAddressFromPosition} size="middle">
            Use my location
        </Button>
    );
}

export default function LocationAutocomplete(props: LocationAutocompleteProps) {
    const { onPlaceChange, value: locationValue, onChange, initialDisplayValue } = props;
    const [showError, setShowError] = useState<number>(0);
    const displayValue = locationValue?.locationDisplayName || initialDisplayValue || undefined;

    const {
        ready,
        value: inputValue,
        suggestions,
        setValue,
        init,
    } = usePlacesAutocomplete({
        initOnMount: false,
        defaultValue: displayValue,
        requestOptions: {
            types: ["locality", "administrative_area_level_3", "postal_code"],
            componentRestrictions: {
                country: "us",
            },
        },
    });
    useGoogleMapsPlacesApi(init);

    useEffect(() => {
        const nextDisplay = locationValue?.locationDisplayName || initialDisplayValue || "";
        setValue(nextDisplay, false);
    }, [initialDisplayValue, locationValue?.locationDisplayName, setValue]);

    const notifyChange = (coords?: Coordinates, displayName?: string) => {
        const updatedValue: LocationValue = {
            coordinates: coords,
            locationDisplayName: displayName,
        };
        onChange?.(updatedValue);
        if (coords && displayName) {
            onPlaceChange?.(coords, displayName);
        }
    };

    const onChangeInput = (data: string) => {
        setValue(data);
    };

    const onSelect = (selectedValue: string, option: DefaultOptionType) => {
        if (option.title) {
            setValue(option.title, false);
            filterUpdateEvent("location", option.title, "dropdown");
        } else {
            console.error(`Missing title for place id ${selectedValue}`);
        }

        getGeocode({ placeId: selectedValue }).then((results) =>
            notifyChange(getLatLng(results[0]), option.title as string)
        );
    };

    const onAutodetect = (coords: Coordinates, locationName: string) => {
        setValue(locationName, false);
        filterUpdateEvent("location", locationName, "autodetect");
        notifyChange(coords, locationName);
    };

    const onClear = () => {
        notifyChange(undefined, undefined);
        setValue("", false);
    };

    const options: DefaultOptionType[] = useMemo(() => {
        if (suggestions.loading && suggestions.status !== "ZERO_RESULTS") {
            return [
                {
                    label: (
                        <>
                            Loading... <Spin />
                        </>
                    ),
                    disabled: true,
                    value: "NULL",
                },
            ];
        }
        return suggestions.data.map((suggestion) => {
            const { structured_formatting: format, place_id: placeId } = suggestion;
            const label = (
                <span>
                    <b>{format.main_text}</b>, {format.secondary_text}
                </span>
            );
            return {
                label,
                title: `${format.main_text}, ${format.secondary_text}`,
                value: placeId,
            };
        });
    }, [suggestions]);
    const notFoundContent = useMemo(() => {
        const { status } = suggestions;
        if (status === "ZERO_RESULTS") {
            return "No Results found";
        }
        if (status === "OK" || status === "") {
            return null;
        }
        return "An error occurred";
    }, [suggestions]);

    let errorAlert = null;
    if (showError !== 0) {
        let errorMessage = "An unknown error occurred.";
        switch (showError) {
            case 1:
                errorMessage = "Location permission was denied. Please enable location services and try again.";
                break;
            case 2:
                errorMessage = "Automatic location detection is not supported on this device.";
                break;
            case 3:
                errorMessage = "There was an issue automatically retrieving your location. Please try again later.";
                break;
            default:
                errorMessage = "An unknown error occurred.";
        }
        errorAlert = <div className="place-autocomplete-error">{errorMessage}</div>;
    }

    return (
        <>
            <div className="place-autocomplete-container">
                <AutoComplete
                    id="location_autocomplete"
                    disabled={!ready}
                    className="place-autocomplete"
                    placeholder="ex. Durham, NC, 27705"
                    onChange={onChangeInput}
                    onSelect={onSelect}
                    notFoundContent={notFoundContent}
                    value={inputValue}
                    options={options}
                    popupRender={PoweredByGoogleWrapper}
                    onClear={onClear}
                    allowClear={{ clearIcon: <CloseSquareFilled /> }}
                />
                <UseCurrentLocation setValue={onAutodetect} onError={setShowError} />
            </div>
            {errorAlert}
        </>
    );
}
